import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DrunkButton } from '../components/DrunkButton';
import { ChatBubble, TypingIndicator } from '../components/ChatBubble';
import { PinInput } from '../components/PinInput';
import { DrunkCard } from '../components/DrunkCard';
import { MODES } from '../components/ModeSelector';
import { useSession } from '../context/SessionContext';
import { Send, Lightbulb, HelpCircle, LogOut, FileText, Users, Camera, Coins, Heart, MapPin, Star, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mode-specific message type configs
const MODE_MESSAGE_TYPES = {
  drunk_ai: [
    { key: 'user', label: '💬 Chat', color: 'bg-cyan' },
    { key: 'idea', label: '💡 Idea', color: 'bg-yellow', icon: Lightbulb },
    { key: 'question', label: 'Should I?', color: 'bg-pink text-white', icon: HelpCircle },
  ],
  party: [
    { key: 'user', label: '💬 Chat', color: 'bg-cyan' },
    { key: 'highlight', label: '✨ Highlight', color: 'bg-yellow', icon: Star },
    { key: 'funny', label: '😂 Funny', color: 'bg-orange-400', icon: null },
  ],
  keepsake: [
    { key: 'user', label: '💬 Message', color: 'bg-rose-300' },
    { key: 'wish', label: '💝 Wish', color: 'bg-rose-400 text-white', icon: Heart },
    { key: 'advice', label: '✨ Advice', color: 'bg-purple-300', icon: Star },
  ],
  roadtrip: [
    { key: 'user', label: '💬 Chat', color: 'bg-sky-300' },
    { key: 'stop', label: '📍 Stop', color: 'bg-emerald-500 text-white', icon: MapPin },
    { key: 'moment', label: '🌄 Moment', color: 'bg-amber-400', icon: Star },
  ],
  event: [
    { key: 'user', label: '💬 Note', color: 'bg-slate-300' },
    { key: 'highlight', label: '📋 Highlight', color: 'bg-indigo-500 text-white', icon: Star },
    { key: 'action', label: '✅ Action', color: 'bg-emerald-400', icon: null },
  ],
};

export const ActiveSession = () => {
  const { sessionCode } = useParams();
  const navigate = useNavigate();
  const { session, messages, setAllMessages, addMessage, clearSession, coinBalance, setCoinBalance, updateMessage, modeConfig, setModeConfig, saveSession } = useSession();
  
  const [input, setInput] = useState('');
  const [messageType, setMessageType] = useState('user');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [reauthPin, setReauthPin] = useState('');
  const [reauthName, setReauthName] = useState('');
  const [reauthEmail, setReauthEmail] = useState('');
  const [reauthStep, setReauthStep] = useState(1);
  const [isReauthenticating, setIsReauthenticating] = useState(false);
  // Photo caption modal state
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  // AI toggle - default ON, stored in localStorage
  const [aiEnabled, setAiEnabled] = useState(() => {
    const saved = localStorage.getItem('aiEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Save AI toggle preference
  const toggleAi = () => {
    const newValue = !aiEnabled;
    setAiEnabled(newValue);
    localStorage.setItem('aiEnabled', JSON.stringify(newValue));
    toast.success(newValue ? '🤖 AI is ON' : '🔇 AI is OFF');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Use ref to track scroll state (avoids re-render issues)
  const userScrolledUp = useRef(false);
  const chatContainerRef = useRef(null);
  const prevMessageCount = useRef(0);

  // Only auto-scroll when NEW messages arrive AND user hasn't scrolled up
  useEffect(() => {
    const newCount = messages.length;
    const hasNewMessages = newCount > prevMessageCount.current;
    prevMessageCount.current = newCount;
    
    // Only scroll if there are new messages and user is at bottom
    if (hasNewMessages && !userScrolledUp.current) {
      scrollToBottom();
    }
  }, [messages]);

  // Scroll to bottom when typing indicator appears (only if at bottom)
  useEffect(() => {
    if (isTyping && !userScrolledUp.current) {
      scrollToBottom();
    }
  }, [isTyping]);

  // Detect if user scrolls up or back to bottom
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    userScrolledUp.current = !isNearBottom;
  };

  useEffect(() => {
    // Validate session and load messages
    const loadSession = async () => {
      // Check if we have a valid session for this session code
      const hasValidSession = session && session.session_code === sessionCode.toUpperCase() && session.pin;
      
      if (!hasValidSession) {
        // Show re-authentication UI instead of redirecting
        setNeedsReauth(true);
        setIsLoading(false);
        return;
      }

      try {
        // Load messages
        const response = await axios.get(
          `${API}/sessions/${sessionCode}/messages?pin=${session.pin}`
        );
        setAllMessages(response.data.messages || []);
        
        // Load coin balance
        const coinsResponse = await axios.get(
          `${API}/sessions/${sessionCode}/coins?pin=${session.pin}`
        );
        const balances = coinsResponse.data.balances || {};
        setCoinBalance(balances[session.member_name] || 10);
        
        // Load session info including mode
        const validateResponse = await axios.get(
          `${API}/sessions/${sessionCode}/validate?pin=${session.pin}`
        );
        if (validateResponse.data.mode_config) {
          setModeConfig(validateResponse.data.mode_config);
        }
      } catch (error) {
        console.error('Failed to load messages:', error);
        if (error.response?.status === 401 || error.response?.status === 404) {
          toast.error("Session invalid or expired");
          clearSession();
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(async () => {
      if (session?.pin) {
        try {
          const response = await axios.get(
            `${API}/sessions/${sessionCode}/messages?pin=${session.pin}`
          );
          setAllMessages(response.data.messages || []);
          
          // Also refresh coin balance
          const coinsResponse = await axios.get(
            `${API}/sessions/${sessionCode}/coins?pin=${session.pin}`
          );
          const balances = coinsResponse.data.balances || {};
          setCoinBalance(balances[session.member_name] || 10);
        } catch (error) {
          // Silent fail for polling
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [sessionCode, session, navigate, setAllMessages, clearSession, setCoinBalance, setModeConfig]);

  const handleSendMessage = async () => {
    if (!input.trim() || !session) return;

    const messageContent = input.trim();
    setInput('');
    if (aiEnabled) setIsTyping(true);
    
    // When user sends a message, always scroll to bottom
    userScrolledUp.current = false;

    try {
      const response = await axios.post(`${API}/sessions/message`, {
        session_code: sessionCode,
        pin: session.pin,
        sender_name: session.member_name,
        content: messageContent,
        message_type: messageType,
        ai_enabled: aiEnabled
      });

      addMessage(response.data.user_message);
      if (response.data.ai_message) {
        addMessage(response.data.ai_message);
      }
      scrollToBottom();
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't send message!";
      toast.error(message);
    } finally {
      setIsTyping(false);
      setMessageType('user');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !session) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Only images allowed, party animal! 📸");
      return;
    }

    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Photo too big! Max 10MB 📷");
      return;
    }

    // Store the file and show caption modal
    setPendingPhoto(file);
    setPhotoCaption('');
    setShowCaptionModal(true);
  };

  const handleCaptionSubmit = async () => {
    if (!pendingPhoto || !session) return;

    setIsUploading(true);
    setShowCaptionModal(false);
    
    // When user uploads, scroll to bottom
    userScrolledUp.current = false;

    const formData = new FormData();
    formData.append('photo', pendingPhoto);
    formData.append('session_code', sessionCode);
    formData.append('pin', session.pin);
    formData.append('sender_name', session.member_name);
    formData.append('caption', photoCaption || '');
    formData.append('ai_enabled', aiEnabled.toString());

    try {
      const response = await axios.post(`${API}/sessions/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      addMessage(response.data.photo_message);
      if (response.data.ai_message) {
        addMessage(response.data.ai_message);
      }
      toast.success("Photo uploaded! 📸🎉");
      scrollToBottom();
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't upload photo!";
      toast.error(message);
    } finally {
      setIsUploading(false);
      setPendingPhoto(null);
      setPhotoCaption('');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCancelPhoto = () => {
    setShowCaptionModal(false);
    setPendingPhoto(null);
    setPhotoCaption('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLeaveSession = () => {
    clearSession();
    navigate('/');
  };

  const handleLike = async (messageId) => {
    if (!session) return;
    
    try {
      const response = await axios.post(`${API}/sessions/like`, {
        session_code: sessionCode,
        pin: session.pin,
        message_id: messageId,
        liker_name: session.member_name
      });
      
      updateMessage(messageId, { likes: response.data.liked_by });
      toast.success(response.data.action === 'liked' ? '❤️ Liked!' : 'Unliked');
    } catch (error) {
      toast.error("Couldn't like that!");
    }
  };

  const handleDonate = async (messageId, recipientName) => {
    if (!session) return;
    
    const amount = prompt(`How many Drunk Coins to tip ${recipientName}? (You have ${coinBalance} 🪙)`);
    if (!amount) return;
    
    const coinAmount = parseInt(amount);
    if (isNaN(coinAmount) || coinAmount < 1) {
      toast.error("Enter a valid number, drunk person!");
      return;
    }
    
    if (coinAmount > coinBalance) {
      toast.error(`You only have ${coinBalance} Drunk Coins! 🪙`);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/sessions/donate`, {
        session_code: sessionCode,
        pin: session.pin,
        message_id: messageId,
        donor_name: session.member_name,
        amount: coinAmount
      });
      
      setCoinBalance(response.data.donor_new_balance);
      updateMessage(messageId, { drunk_coins: response.data.message_total_coins });
      toast.success(`🪙 Tipped ${coinAmount} Drunk Coin${coinAmount > 1 ? 's' : ''} to ${recipientName}!`);
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't send coins!";
      toast.error(message);
    }
  };

  // Handle deleting own messages
  const handleDelete = async (messageId) => {
    if (!session) return;
    
    if (!window.confirm("Delete this message?")) return;
    
    try {
      await axios.delete(`${API}/sessions/message/${messageId}`, {
        data: {
          session_code: sessionCode,
          pin: session.pin,
          requester_name: session.member_name
        }
      });
      
      // Remove message from local state
      setAllMessages(messages.filter(m => m.id !== messageId));
      toast.success("Message deleted 🗑️");
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't delete message!";
      toast.error(message);
    }
  };

  // Handle re-authentication for returning users
  const handleReauthenticate = async () => {
    if (reauthStep === 1 && reauthPin.length !== 4) {
      toast.error("Enter the 4-digit PIN");
      return;
    }
    if (reauthStep === 2 && !reauthName.trim()) {
      toast.error("Enter your name");
      return;
    }
    if (reauthStep === 3 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reauthEmail)) {
      toast.error("Enter a valid email");
      return;
    }

    if (reauthStep < 3) {
      setReauthStep(reauthStep + 1);
      return;
    }

    setIsReauthenticating(true);
    try {
      const response = await axios.post(`${API}/sessions/join`, {
        session_code: sessionCode.toUpperCase(),
        pin: reauthPin,
        member_name: reauthName.trim(),
        member_email: reauthEmail.trim().toLowerCase()
      });
      
      saveSession({
        ...response.data,
        pin: reauthPin,
        member_name: reauthName.trim(),
        member_email: reauthEmail.trim().toLowerCase()
      });
      
      setNeedsReauth(false);
      setIsLoading(true);
      toast.success("Welcome back! 🎉");
      
      // Reload the session data
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't rejoin the party!";
      toast.error(message);
    } finally {
      setIsReauthenticating(false);
    }
  };

  // Get mode-specific config
  const mode = session?.mode || 'drunk_ai';
  const currentModeConfig = modeConfig || MODES[mode] || MODES.drunk_ai;
  const messageTypes = MODE_MESSAGE_TYPES[mode] || MODE_MESSAGE_TYPES.drunk_ai;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🍺</div>
          <p className="font-heading text-2xl">Loading the party...</p>
        </div>
      </div>
    );
  }

  // Show re-authentication UI if session doesn't match
  if (needsReauth) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <DrunkCard variant="default" className="text-center p-5">
            <Lock className="w-12 h-12 mx-auto mb-3 text-pink" />
            <h2 className="font-heading text-2xl font-bold mb-2">
              Rejoin Session 🎉
            </h2>
            <p className="font-body text-sm text-black/70 mb-4">
              Session: <span className="font-mono font-bold">{sessionCode.toUpperCase()}</span>
            </p>
            
            {reauthStep === 1 && (
              <div className="bounce-in">
                <p className="font-body text-sm mb-4">Enter the PIN to rejoin</p>
                <div className="mb-6">
                  <PinInput
                    value={reauthPin}
                    onChange={setReauthPin}
                    length={4}
                  />
                </div>
                <DrunkButton
                  variant="primary"
                  size="large"
                  onClick={handleReauthenticate}
                  disabled={reauthPin.length !== 4}
                  className="w-full py-4"
                  data-testid="reauth-pin-btn"
                >
                  Next: Your Name 👤
                </DrunkButton>
              </div>
            )}
            
            {reauthStep === 2 && (
              <div className="bounce-in">
                <p className="font-body text-sm mb-4">What's your name?</p>
                <input
                  type="text"
                  placeholder="Your name"
                  value={reauthName}
                  onChange={(e) => setReauthName(e.target.value)}
                  className="w-full p-4 text-lg border-2 border-black rounded-xl mb-4 font-body shadow-brutal focus:outline-none focus:border-pink"
                  maxLength={50}
                  data-testid="reauth-name-input"
                />
                <DrunkButton
                  variant="primary"
                  size="large"
                  onClick={handleReauthenticate}
                  disabled={!reauthName.trim()}
                  className="w-full py-4"
                  data-testid="reauth-name-btn"
                >
                  Next: Your Email 📧
                </DrunkButton>
              </div>
            )}
            
            {reauthStep === 3 && (
              <div className="bounce-in">
                <p className="font-body text-sm mb-4">Enter your email to rejoin</p>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={reauthEmail}
                  onChange={(e) => setReauthEmail(e.target.value)}
                  className="w-full p-4 text-lg border-2 border-black rounded-xl mb-4 font-body shadow-brutal focus:outline-none focus:border-pink"
                  maxLength={100}
                  data-testid="reauth-email-input"
                />
                <DrunkButton
                  variant="primary"
                  size="large"
                  onClick={handleReauthenticate}
                  disabled={isReauthenticating || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reauthEmail)}
                  className="w-full py-4"
                  data-testid="reauth-submit-btn"
                >
                  {isReauthenticating ? 'Rejoining...' : 'Rejoin Party! 🎉'}
                </DrunkButton>
              </div>
            )}
            
            <button
              onClick={() => navigate('/')}
              className="mt-4 text-sm text-black/50 hover:text-black"
              data-testid="reauth-back-btn"
            >
              ← Back to Home
            </button>
          </DrunkCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Header */}
      <header className="bg-white border-b-2 border-black p-3 shadow-brutal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{currentModeConfig.emoji || '🍺'}</span>
            <div>
              <h1 className="font-heading text-lg font-bold">{currentModeConfig.name || 'Drunk AI'}</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5">
            {/* Drunk Coin Balance */}
            <div className="flex items-center gap-1 bg-yellow px-2 py-1 rounded-full border-2 border-black text-xs" data-testid="coin-balance">
              <Coins className="w-3 h-3" />
              <span className="font-bold">{coinBalance}</span>
            </div>
            
            <div className="flex items-center gap-1 bg-cyan px-2 py-1 rounded-full border-2 border-black text-xs">
              <Users className="w-3 h-3" />
              <span className="font-bold">{session?.members?.length || 1}</span>
            </div>
            
            <DrunkButton
              variant="accent"
              size="small"
              onClick={() => navigate(`/session/${sessionCode}/summary`)}
              data-testid="view-summary-btn"
              className="!py-1.5 !px-2 text-xs"
            >
              <FileText className="w-3 h-3" />
            </DrunkButton>
            
            <DrunkButton
              variant="ghost"
              size="small"
              onClick={handleLeaveSession}
              data-testid="leave-session-btn"
              className="!py-1.5 !px-2"
            >
              <LogOut className="w-4 h-4" />
            </DrunkButton>
          </div>
        </div>
      </header>

      {/* Persistent Party Info Reminder - Always visible! */}
      <div className={`border-b-2 border-black py-1.5 px-3 ${mode === 'keepsake' ? 'bg-rose-200' : mode === 'roadtrip' ? 'bg-emerald-200' : mode === 'event' ? 'bg-indigo-200' : mode === 'party' ? 'bg-orange-200' : 'bg-yellow'}`}>
        <div className="flex flex-wrap items-center justify-center gap-2 text-center">
          <div className="flex items-center gap-1">
            <span className="font-bold text-xs">{currentModeConfig.emoji}</span>
            <span className="font-mono font-bold text-sm bg-white px-2 py-0.5 rounded border-2 border-black">{sessionCode}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-bold text-xs">🔐</span>
            <span className="font-mono font-bold text-sm bg-white px-2 py-0.5 rounded border-2 border-black">{session?.pin || '****'}</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div 
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-3 pb-44" 
        data-testid="messages-area"
      >
        <div className="max-w-2xl mx-auto">
          {messages.map((msg, index) => (
            <ChatBubble 
              key={msg.id || index} 
              message={msg} 
              currentUser={session?.member_name}
              onLike={handleLike}
              onDonate={handleDonate}
              onDelete={handleDelete}
            />
          ))}
          {isTyping && <TypingIndicator aiName={currentModeConfig.name || 'Drunk AI'} />}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Fixed at bottom, mobile optimized */}
      <div className="fixed bottom-0 left-0 right-0 bg-cream border-t-2 border-black p-3 safe-area-bottom">
        <div className="max-w-2xl mx-auto">
          {/* Message Type Toggles - Dynamic based on mode */}
          <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1 -mx-1 px-1">
            {messageTypes.map((type) => (
              <button
                key={type.key}
                onClick={() => setMessageType(type.key)}
                className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full border-2 border-black text-xs font-bold transition-all ${
                  messageType === type.key ? type.color : 'bg-white'
                }`}
                data-testid={`type-${type.key}-btn`}
              >
                {type.icon && <type.icon className="w-3 h-3" />}
                {type.label}
              </button>
            ))}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full border-2 border-black text-xs font-bold transition-all bg-white hover:bg-gray-100 disabled:opacity-50"
              data-testid="upload-photo-btn"
            >
              <Camera className="w-3 h-3" /> {isUploading ? '...' : 'Photo'}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoUpload}
              accept="image/*"
              className="hidden"
              data-testid="photo-file-input"
            />
            {/* AI Toggle Button */}
            <button
              onClick={toggleAi}
              className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full border-2 border-black text-xs font-bold transition-all ${
                aiEnabled 
                  ? 'bg-emerald-400 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}
              data-testid="ai-toggle-btn"
              title={aiEnabled ? 'AI responses ON - click to turn OFF' : 'AI responses OFF - click to turn ON'}
            >
              {aiEnabled ? '🤖 AI' : '🔇 AI'}
            </button>
          </div>

          {/* Input Field - Large touch target */}
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                messageType === 'idea' 
                  ? "Your million dollar idea..." 
                  : messageType === 'question'
                  ? "Should I text my ex?"
                  : "What's on your mind?"
              }
              className="flex-1 p-3 text-base border-2 border-black rounded-xl resize-none font-body shadow-brutal focus:outline-none focus:border-pink min-h-[48px]"
              rows={1}
              data-testid="message-input"
            />
            <DrunkButton
              variant="primary"
              onClick={handleSendMessage}
              disabled={!input.trim() || isTyping}
              className="self-end !p-3 min-w-[48px] min-h-[48px]"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </DrunkButton>
          </div>
        </div>
      </div>

      {/* Photo Caption Modal */}
      {showCaptionModal && pendingPhoto && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="caption-modal">
          <div className="bg-white rounded-2xl border-4 border-black shadow-brutal max-w-md w-full p-5 bounce-in">
            <h3 className="font-heading text-xl font-bold mb-3 text-center">
              📸 Add a Caption
            </h3>
            
            {/* Photo Preview */}
            <div className="mb-4 rounded-xl overflow-hidden border-2 border-black">
              <img 
                src={URL.createObjectURL(pendingPhoto)} 
                alt="Preview" 
                className="w-full h-48 object-cover"
              />
            </div>
            
            {/* Caption Input */}
            <textarea
              value={photoCaption}
              onChange={(e) => setPhotoCaption(e.target.value)}
              placeholder={
                currentModeConfig.name === 'Keepsake' 
                  ? "e.g., Gift from Sharon - beautiful bread maker!" 
                  : currentModeConfig.name === 'Road Trip Buddy'
                  ? "e.g., Stopped at the Grand Canyon!"
                  : "What's happening in this photo?"
              }
              className="w-full p-3 text-base border-2 border-black rounded-xl resize-none font-body shadow-brutal focus:outline-none focus:border-pink mb-4"
              rows={2}
              maxLength={200}
              autoFocus
              data-testid="caption-input"
            />
            
            <p className="text-xs text-black/50 mb-4 font-body">
              💡 Tip: Mention names and items for smart tracking!<br/>
              &ldquo;Gift from Jerry&rdquo;, &ldquo;Sharon&apos;s advice&rdquo;, &ldquo;Wish from Mom&rdquo;
            </p>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <DrunkButton
                variant="secondary"
                onClick={handleCancelPhoto}
                className="flex-1"
                data-testid="cancel-caption-btn"
              >
                Cancel
              </DrunkButton>
              <DrunkButton
                variant="primary"
                onClick={handleCaptionSubmit}
                disabled={isUploading}
                className="flex-1"
                data-testid="submit-caption-btn"
              >
                {isUploading ? 'Uploading...' : 'Upload 📸'}
              </DrunkButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveSession;
